import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FileWarning, Copy, RefreshCw, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('fr-CA', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : 'Jamais');

const Row = ({ v, onCheck, checking }) => (
  <tr className="border-b border-slate-100 last:border-0" data-testid={`carfax-missing-row-${v.id}`}>
    <td className="py-2.5 pr-3 font-semibold text-blue-600 whitespace-nowrap">#{v.stock_number || '—'}</td>
    <td className="py-2.5 pr-3 text-slate-800">{v.year} {v.make} {v.model} <span className="text-slate-400">{v.trim}</span></td>
    <td className="py-2.5 pr-3">
      {v.vin ? (
        <button onClick={() => { navigator.clipboard.writeText(v.vin); toast.success('VIN copié'); }} className="font-mono text-xs bg-slate-100 text-slate-700 hover:bg-slate-200 px-2 py-1 rounded inline-flex items-center gap-1" data-testid={`carfax-missing-copy-${v.id}`}>
          {v.vin} <Copy className="h-3 w-3" />
        </button>
      ) : <span className="text-red-500 text-xs">Aucun VIN</span>}
    </td>
    <td className="py-2.5 pr-3 text-xs text-slate-500 whitespace-nowrap">{fmtDate(v.carfax_checked_at)}</td>
    <td className="py-2.5 text-right">
      <Button size="sm" variant="outline" className="bg-white text-slate-700 border-slate-300 hover:bg-slate-50" disabled={!v.vin || checking} onClick={() => onCheck(v)} data-testid={`carfax-missing-check-${v.id}`}>
        {checking ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
      </Button>
    </td>
  </tr>
);

export const CarfaxMissingButton = ({ headers, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [list, setList] = useState(null);
  const [checking, setChecking] = useState(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/carfax/missing`, { headers });
      setList(data.vehicles);
    } catch { setList([]); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const check = async (v) => {
    setChecking(v.id);
    try {
      const { data } = await axios.post(`${API}/admin/carfax/sync/${v.id}`, {}, { headers });
      toast[data.updated ? 'success' : 'info'](data.updated ? `Rapport trouvé pour #${v.stock_number}` : `Toujours aucun rapport pour #${v.stock_number}`);
      await load();
      if (data.updated) onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Vérification impossible');
    } finally { setChecking(null); }
  };

  const copyAll = () => {
    const vins = (list || []).map((v) => v.vin).filter(Boolean).join('\n');
    navigator.clipboard.writeText(vins);
    toast.success(`${vins.split('\n').filter(Boolean).length} VIN copiés`);
  };

  const count = list?.length ?? 0;
  return (
    <>
      <Button variant="outline" onClick={() => { setOpen(true); load(); }} className={count ? 'border-amber-300 text-amber-700 hover:bg-amber-50' : 'border-slate-300'} data-testid="carfax-missing-button">
        <FileWarning className="h-4 w-4 mr-2" /> Sans CARFAX ({list === null ? '…' : count})
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto bg-white text-slate-900 border-slate-200" data-testid="carfax-missing-dialog">
          <DialogHeader>
            <DialogTitle className="text-slate-900">Véhicules en stock sans rapport CARFAX ({count})</DialogTitle>
            <DialogDescription className="text-slate-500">Aucun rapport n'est commandé automatiquement. Copiez les VIN pour commander ceux que vous voulez sur CARFAX, puis cliquez ↻ pour vérifier.</DialogDescription>
          </DialogHeader>
          {count > 0 && (
            <div className="flex justify-end">
              <Button size="sm" variant="outline" className="bg-white text-slate-700 border-slate-300 hover:bg-slate-50" onClick={copyAll} data-testid="carfax-missing-copy-all"><Copy className="h-3.5 w-3.5 mr-2" /> Copier tous les VIN</Button>
            </div>
          )}
          {list === null ? (
            <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-blue-500" /></div>
          ) : count === 0 ? (
            <p className="py-10 text-center text-slate-500" data-testid="carfax-missing-empty">Tous les véhicules en stock ont un rapport CARFAX.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500 border-b border-slate-200">
                    <th className="pb-2 pr-3">Stock</th><th className="pb-2 pr-3">Véhicule</th><th className="pb-2 pr-3">VIN</th><th className="pb-2 pr-3">Dernière vérif.</th><th className="pb-2" />
                  </tr>
                </thead>
                <tbody>{list.map((v) => <Row key={v.id} v={v} onCheck={check} checking={checking === v.id} />)}</tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
